import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Search, X } from 'lucide-react';

const TreatmentSelectorWithFilters = ({ 
  treatments = [], 
  selectedTreatment, 
  onSelect, 
  disabled = false,
  className = '',
  label = 'Select Treatment',
  placeholder = 'Search treatments...'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedTier, setSelectedTier] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Extract unique treatment types and tiers
  const treatmentTypes = useMemo(() => {
    const types = new Set();
    treatments.forEach(t => {
      if (t.type) types.add(t.type);
    });
    return Array.from(types).sort();
  }, [treatments]);

  const treatmentTiers = useMemo(() => {
    const tiers = new Set();
    treatments.forEach(t => {
      if (t.tier_level) tiers.add(t.tier_level);
    });
    return Array.from(tiers).sort();
  }, [treatments]);

  // Filter treatments by type, tier, and search query
  const filteredTreatments = useMemo(() => {
    let filtered = treatments;

    // Filter by type
    if (selectedType) {
      filtered = filtered.filter(t => t.type === selectedType);
    }

    // Filter by tier
    if (selectedTier) {
      filtered = filtered.filter(t => t.tier_level === selectedTier);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(t => 
        t.name.toLowerCase().includes(query) ||
        (t.short_name && t.short_name.toLowerCase().includes(query)) ||
        (t.type && t.type.toLowerCase().includes(query)) ||
        (t.tier_level && t.tier_level.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [treatments, selectedType, selectedTier, searchQuery]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleTreatmentSelect = (treatment) => {
    onSelect(treatment);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleOpen = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  const clearFilters = () => {
    setSelectedType('');
    setSelectedTier('');
    setSearchQuery('');
  };

  // Get selected treatment object - case-insensitive matching
  const selectedTreatmentObj = selectedTreatment 
    ? treatments.find(t => t.name.toUpperCase() === selectedTreatment.toUpperCase())
    : null;

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={handleOpen}
        disabled={disabled}
        className={`
          w-full p-2.5 text-sm border rounded-md text-left flex items-center justify-between
          ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white hover:border-[#E4C984] cursor-pointer'}
          ${isOpen ? 'border-[#E4C984] ring-2 ring-[#E4C984]' : 'border-gray-300'}
          focus:outline-none focus:ring-2 focus:ring-[#E4C984] focus:border-transparent
        `}
      >
        <span className={selectedTreatmentObj ? 'text-gray-900' : 'text-gray-400'}>
          {selectedTreatmentObj ? selectedTreatmentObj.name : label}
        </span>
        <svg 
          className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-[500px] flex flex-col">
          {/* Search Bar */}
          <div className="p-3 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder={placeholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E4C984] focus:border-transparent"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2"
                >
                  <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                </button>
              )}
            </div>
          </div>

          {/* Type and Tier Filters */}
          <div className="px-3 py-2 border-b border-gray-200 bg-gray-50">
            <div className="grid grid-cols-2 gap-2 mb-2">
              {/* Type Filter */}
              {treatmentTypes.length > 0 && (
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E4C984] focus:border-transparent bg-white"
                >
                  <option value="">All Types</option>
                  {treatmentTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              )}

              {/* Tier Filter */}
              {treatmentTiers.length > 0 && (
                <select
                  value={selectedTier}
                  onChange={(e) => setSelectedTier(e.target.value)}
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E4C984] focus:border-transparent bg-white"
                >
                  <option value="">All Tiers</option>
                  {treatmentTiers.map((tier) => (
                    <option key={tier} value={tier}>
                      {tier}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Filter Status and Clear */}
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-500">
                {filteredTreatments.length} of {treatments.length} treatments
              </span>
              {(selectedType || selectedTier || searchQuery) && (
                <button
                  onClick={clearFilters}
                  className="text-[#E4C984] hover:text-[#d4b874] font-medium"
                >
                  Clear Filters
                </button>
              )}
            </div>
          </div>

          {/* Treatment List */}
          <div className="overflow-y-auto max-h-[300px]">
            {filteredTreatments.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-500">
                No treatments found
              </div>
            ) : (
              <div className="py-1">
                {filteredTreatments.map((treatment) => (
                  <button
                    key={treatment.id || treatment.name}
                    onClick={() => handleTreatmentSelect(treatment)}
                    className={`
                      w-full px-4 py-2.5 text-left hover:bg-gray-50 transition-colors
                      ${treatment.name === selectedTreatment ? 'bg-[#FFFBF0]' : ''}
                    `}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm text-gray-900 truncate">
                          {treatment.name}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500">
                          {treatment.duration && (
                            <span>{treatment.duration} min</span>
                          )}
                          {(treatment.base_price || treatment.tier_value) && (
                            <span>• ₹{treatment.base_price || treatment.tier_value || 0}</span>
                          )}
                          {treatment.type && (
                            <span className="px-1.5 py-0.5 bg-gray-100 rounded">
                              {treatment.type}
                            </span>
                          )}
                          {treatment.tier_level && (
                            <span className="px-1.5 py-0.5 bg-[#E4C984] bg-opacity-20 text-[#8B7355] rounded font-medium">
                              {treatment.tier_level}
                            </span>
                          )}
                        </div>
                      </div>
                      {treatment.name === selectedTreatment && (
                        <svg 
                          className="w-5 h-5 text-[#E4C984] flex-shrink-0 ml-2" 
                          fill="currentColor" 
                          viewBox="0 0 20 20"
                        >
                          <path 
                            fillRule="evenodd" 
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" 
                            clipRule="evenodd" 
                          />
                        </svg>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TreatmentSelectorWithFilters;

